// Состояние раздела «Спутники» в query-параметрах URL: рефреш сохраняет вид,
// пересланная ссылка открывает в точности тот же вид (выбранный спутник, момент
// времени, фасетный фильтр).
//
// Чистые функции (без DOM/React) — тестируются отдельно. Центр/зум карты живут в
// тех же query-параметрах, но ими владеет useMapView (map/mapView.ts) — здесь мы
// их НЕ трогаем: writeSatUrl копирует prev и сливает ТОЛЬКО свои 6 ключей, не
// затирая lng/lat/zoom и прочее (тот же приём, что applyModelsState/writeViewCtx).

import { FILTER_DIMS, EMPTY_FILTER, type FilterDim, type FilterState } from './satFilter'

export interface SatUrlState {
  /** NORAD выбранного спутника; null — выбора нет. */
  selectedNorad: string | null
  /** Зафиксированный момент; null — живой режим «сейчас». */
  selectedTime: Date | null
  /** Состояние фасетного фильтра (5 измерений). */
  filter: FilterState
}

// Измерение фильтра → короткий ключ URL. Держим единой константой, чтобы parse и
// write не разъезжались (как FILTER_PROP в satFilter.ts). Дефолтные/пустые
// значения не пишем ради чистого URL.
export const FILTER_PARAM: Record<FilterDim, string> = {
  category: 'cat',
  company: 'co',
  orbit_class: 'orb',
  legal_entity: 'leg',
  status: 'st',
  source: 'src',
}

/** Все ключи, которыми владеет раздел «Спутники» (для чистого слияния в URL). */
export const SAT_URL_KEYS = ['sat', 't', ...FILTER_DIMS.map(d => FILTER_PARAM[d])] as const

/** decodeURIComponent, который не роняет страницу на кривом URL (правленном руками). */
function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s)
  } catch {
    return s
  }
}

/** Читает состояние раздела из URL (сид при монтировании: шаринг/рефреш).
 *  Битый `t` (непарсимый / NaN) → null (живой режим), без исключения.
 *  Отсутствующий ключ фильтра → []. Значения разделены запятой, пустые отброшены,
 *  каждое декодируется (обратно к encodeURIComponent в writeSatUrl). */
export function parseSatUrl(params: URLSearchParams): SatUrlState {
  const sat = params.get('sat')

  const tRaw = params.get('t')
  let selectedTime: Date | null = null
  if (tRaw) {
    const d = new Date(tRaw)
    if (!Number.isNaN(d.getTime())) selectedTime = d
  }

  // Свежий объект со всеми 5 измерениями (форма = EMPTY_FILTER), чтобы round-trip
  // с пустым URL давал именно EMPTY_FILTER.
  const filter = { ...EMPTY_FILTER } as FilterState
  for (const dim of FILTER_DIMS) {
    const raw = params.get(FILTER_PARAM[dim])
    filter[dim] = raw ? raw.split(',').filter(Boolean).map(safeDecode) : []
  }

  return { selectedNorad: sat ? sat : null, selectedTime, filter }
}

/** Дописывает ключи раздела к параметрам страницы, не затирая чужие (lng/lat/zoom
 *  и т.п.). Возвращает НОВЫЙ URLSearchParams (копия prev), вход не мутирует. Дефолт/
 *  пусто → ключ удаляется (чистый URL). Значения фильтра кодируются по одному —
 *  запятая/пробел внутри значения не спутается с разделителем. Round-trip с
 *  parseSatUrl. */
export function writeSatUrl(params: URLSearchParams, s: SatUrlState): URLSearchParams {
  const out = new URLSearchParams(params)

  if (s.selectedNorad) out.set('sat', s.selectedNorad)
  else out.delete('sat')

  // toISOString роняет исключение на невалидной Date — пишем только валидный момент.
  if (s.selectedTime && !Number.isNaN(s.selectedTime.getTime())) {
    out.set('t', s.selectedTime.toISOString())
  } else {
    out.delete('t')
  }

  for (const dim of FILTER_DIMS) {
    const key = FILTER_PARAM[dim]
    const vals = s.filter[dim]
    if (vals && vals.length) out.set(key, vals.map(encodeURIComponent).join(','))
    else out.delete(key)
  }

  return out
}
